import { useEffect, useState } from "react";

import { fetchRoute } from "../services/geolocation";
import type { RouteSummary, TravelMode } from "../types";

export type RouteState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; route: RouteSummary }
  | { status: "error"; error: Error };

type RouteOptions = {
  via?: string[];
  mode?: TravelMode;
  enabled?: boolean;
};

type Settled = { key: string; state: RouteState };

export function useRoute(
  from: string | null,
  to: string | null,
  { via = [], mode, enabled = true }: RouteOptions = {},
): RouteState {
  const [settled, setSettled] = useState<Settled | null>(null);

  const viaKey = via.join(";");
  const key = from && to ? `${from}|${to}|${viaKey}|${mode ?? ""}` : null;

  useEffect(() => {
    if (!enabled || !from || !to || null === key) {
      return;
    }

    const controller = new AbortController();

    fetchRoute(
      { from, to, via: viaKey ? viaKey.split(";") : [], mode },
      { signal: controller.signal },
    )
      .then((route) => setSettled({ key, state: { status: "ready", route } }))
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setSettled({
            key,
            state: {
              status: "error",
              error: error instanceof Error ? error : new Error(String(error)),
            },
          });
        }
      });

    return () => controller.abort();
  }, [enabled, from, to, viaKey, mode, key]);

  if (null === key || !enabled) {
    return { status: "idle" };
  }

  return settled?.key === key ? settled.state : { status: "loading" };
}
